import { useTranslation } from '../i18n';

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Typography, message, Alert } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { homePathFor } from '../config/paths';
import authService from '../services/authService';
import AuthLayout from '../components/AuthLayout';
import InlineCaptcha from '../components/InlineCaptcha';
import OAuthButtons, { oauthErrorMessage } from '../components/OAuthButtons';

const { Text } = Typography;

const Login = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { login, verify2fa, oauthExchange, currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [messageApi, contextHolder] = message.useMessage();

    // 两步验证挑战（登录密码通过后由后端下发）：{ challengeToken, remember }
    const [twoFactor, setTwoFactor] = useState(null);
    const [useBackupCode, setUseBackupCode] = useState(false);

    // 邮箱未验证的 pending 账号：展示"重发验证邮件"入口
    const [resendEmail, setResendEmail] = useState(null);
    const [resendLoading, setResendLoading] = useState(false);

    // Google / GitHub 回调：#oauth_code=（一次性，换令牌；开了 2FA 进入验证码步骤）或 #oauth_error=
    const oauthHandled = React.useRef(false);
    React.useEffect(() => {
        if (oauthHandled.current || !window.location.hash) return;
        const params = new URLSearchParams(window.location.hash.slice(1));
        const code = params.get('oauth_code');
        const error = params.get('oauth_error');
        const provider = params.get('oauth_new'); // set by the callback when this sign-in created the account
        if (!code && !error) return;
        oauthHandled.current = true;
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        if (error) {
            messageApi.error(oauthErrorMessage(error, t));
            return;
        }
        (async () => {
            setLoading(true);
            try {
                const result = await oauthExchange(code);
                if (result.success && provider) {
                    window.dispatchEvent(new CustomEvent('yc:signup', { detail: { method: provider } }));
                }
                if (result.success && result.requires2fa) {
                    setTwoFactor({ challengeToken: result.challengeToken, remember: true });
                    setUseBackupCode(false);
                } else if (result.success) {
                    message.success(t('Signed in successfully'));
                } else {
                    messageApi.error(result.message || t('Sign-in failed'));
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [oauthExchange, messageApi, t]);

    // 如果用户已登录，根据角色跳转到相应页面
    // 优先级: ?return_to= 查询参数 > React Router state.from > 默认 dashboard
    React.useEffect(() => {
        if (currentUser) {
            let target = null;

            // 1) Explicit ?return_to= from the global 401 interceptor.
            try {
                const params = new URLSearchParams(location.search);
                const returnTo = params.get('return_to');
                if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
                    // Allow only same-origin paths. Reject protocol-relative
                    // (//evil.com) and external URLs.
                    target = returnTo;
                }
            } catch (_) { /* ignore malformed */ }

            // 2) React Router state (set by PrivateRoute's <Navigate state={{from}}/>)
            if (!target) {
                target = location.state?.from?.pathname || null;
            }

            // 3) Default dashboard for the role
            if (!target) {
                target = homePathFor(currentUser);
            }

            navigate(target, { replace: true });
        }
    }, [currentUser, navigate, location.state, location.search]);

    const onFinish = async (values) => {
        try {
            setLoading(true);
            setResendEmail(null);
            const { username, password, remember } = values;
            // 验证码由 RateLimitMiddleware 触发的 captcha.la 弹窗处理（见 CaptchaModal），
            // 登录请求命中 430 时会弹出验证，通过后自动带 token 重试。
            const result = await login(username, password, null, remember);

            if (result.success) {
                if (result.requires2fa) {
                    // 密码正确但账号开启了 2FA：切换到验证码输入步骤
                    setTwoFactor({ challengeToken: result.challengeToken, remember });
                    setUseBackupCode(false);
                    return;
                }
                message.success(t('Signed in successfully'));
                // 登录成功后会触发 useEffect 重定向
            } else if (result.resendable && result.email) {
                // pending 且邮箱未验证：提供重发验证邮件入口
                setResendEmail(result.email);
            }
        } catch (error) {
            messageApi.error(t('Sign-in failed: {{value}}', { value: error.message || t('Unknown error') }));
        } finally {
            setLoading(false);
        }
    };

    const onVerify2fa = async (values) => {
        if (!twoFactor) return;
        try {
            setLoading(true);
            const result = await verify2fa(twoFactor.challengeToken, values.code, twoFactor.remember);
            if (result.success) {
                message.success(t('Signed in successfully'));
                // 登录成功后会触发 useEffect 重定向
            }
        } catch (error) {
            messageApi.error(t('Two-factor authentication failed: {{value}}', { value: error.message || t('Unknown error') }));
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        if (!resendEmail) return;
        try {
            setResendLoading(true);
            const result = await authService.resendVerification(resendEmail);
            if (result.success) {
                messageApi.success(result.message || t('Verification email resent. Please check your inbox'));
                setResendEmail(null);
            } else {
                messageApi.error(result.message || t('Failed to send verification email'));
            }
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <AuthLayout title={t('Sign in')} subtitle={t('Sign in to the Yeap Cloud console')}>
            {contextHolder}

                {twoFactor ? (
                    // ============ 两步验证步骤 ============
                    <Form
                        name="login-2fa"
                        onFinish={onVerify2fa}
                        size="large"
                        key={useBackupCode ? 'backup' : 'totp'}
                    >
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <Text>
                                {useBackupCode
                                    ? t('Please enter an unused backup code')
                                    : t('Enter the 6-digit code from your authenticator app')}
                            </Text>
                        </div>

                        <Form.Item
                            name="code"
                            rules={[
                                { required: true, message: useBackupCode ? t('Please enter a backup code!') : t('Enter the verification code!') },
                                useBackupCode
                                    ? { pattern: /^[a-fA-F0-9]{10}$/, message: t('Backup codes are 10 characters long') }
                                    : { pattern: /^\d{6}$/, message: t('The verification code must be 6 digits') }
                            ]}
                        >
                            <Input
                                prefix={<SafetyOutlined />}
                                placeholder={useBackupCode ? t('Backup codes') : t('6-digit verification code')}
                                maxLength={useBackupCode ? 10 : 6}
                                autoFocus
                                autoComplete="one-time-code"
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                                {t('Verify and sign in')}
                            </Button>
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                                <Button type="link" style={{ padding: 0 }} onClick={() => setUseBackupCode(!useBackupCode)}>
                                    {useBackupCode ? t('Use verification code') : t('Use backup code')}
                                </Button>
                                <Button type="link" style={{ padding: 0 }} onClick={() => setTwoFactor(null)}>
                                    {t('Back to sign in')}
                                </Button>
                            </div>
                        </Form.Item>
                    </Form>
                ) : (
                    // ============ 账号密码步骤 ============
                    <>
                        {resendEmail && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message={t('Email not yet verified')}
                                description={
                                    <span>
                                        {t('Please verify your email before signing in. Didn\'t receive the email?')}{' '}
                                        <Button
                                            type="link"
                                            style={{ padding: 0 }}
                                            loading={resendLoading}
                                            onClick={handleResendVerification}
                                        >
                                            {t('Resend verification email')}
                                        </Button>
                                    </span>
                                }
                            />
                        )}

                        <Form
                            name="login"
                            initialValues={{ remember: true }}
                            onFinish={onFinish}
                            size="large"
                        >
                            <Form.Item
                                name="username"
                                rules={[{ required: true, message: t('Enter a username!') }]}
                            >
                                <Input prefix={<UserOutlined />} placeholder={t('Username')} />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: t('Please enter a password!') }]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder={t('Password')} />
                            </Form.Item>

                            <Form.Item>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Form.Item name="remember" valuePropName="checked" noStyle>
                                        <Checkbox>{t('Remember me')}</Checkbox>
                                    </Form.Item>
                                    <Link to="/reset-password">{t('Forgot password?')}</Link>
                                </div>
                            </Form.Item>

                            <InlineCaptcha action="login" />
                            <Form.Item>
                                <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                                    {t('Sign in')}
                                </Button>
                                <div style={{ marginTop: 16 }}>
                                    <OAuthButtons />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    {t('Don\'t have an account? ')}<Link to="/register">{t('Register now')}</Link>
                                </div>
                            </Form.Item>
                        </Form>
                    </>
                )}
        </AuthLayout>
    );
};

export default Login;
